import { FormUseCase } from '@dermloop/static-values';
import { ExactlyOneDefined } from '@dermloop/utils';
import { Transform } from 'class-transformer';
import {
  IsEnum,
  IsNotEmptyObject,
  IsObject,
  IsUUID,
  ValidateIf,
} from 'class-validator';

/** Query for resolving the template assigned to the caller's organisation. */
export class ResolveFormTemplateAssignmentQueryDto {
  /** `first-triage` is the deprecated name older Capture versions still send. */
  @Transform(({ value }) =>
    value === 'first-triage' ? FormUseCase.Triage : value,
  )
  @IsEnum(FormUseCase)
  useCase: FormUseCase;

  /**
   * Condition-bound use cases key on the condition being worked on;
   * condition-agnostic ones (the simple e-consult) on a speciality. Both key
   * columns are nullable, so a query carrying both could match rows on either
   * axis with no rule for which wins — requiring exactly one keeps that rule
   * unnecessary. `@IsOptional()` cannot express that: it would also skip the
   * exclusivity check on the request that omits both keys.
   */
  @ValidateIf((dto) => dto.specialityId === undefined)
  @IsUUID()
  conditionTypeId?: string;

  @ExactlyOneDefined<ResolveFormTemplateAssignmentQueryDto>(['conditionTypeId'])
  @ValidateIf((dto) => dto.specialityId !== undefined)
  @IsUUID()
  specialityId?: string;
}

/** An immutable document for rendering and exact-version submission pinning. */
export class FormTemplateDto {
  /** Exact version UUID, including on the released GET /form-templates/:id. */
  id: string;
  formTemplateId: string;
  version: number;
  formLanguageVersion: string;
  /** The form-language document — cast to `FormTemplateDocument` on the client. */
  document: Record<string, unknown>;
}

export class PublishFormTemplateDto {
  @IsObject()
  @IsNotEmptyObject()
  document: Record<string, unknown>;
}

export class CreateFormTemplateDto extends ResolveFormTemplateAssignmentQueryDto {
  @ValidateIf((dto) => dto.federationId !== undefined)
  @IsUUID()
  federationId?: string;

  @ValidateIf((dto) => dto.medicalUnitId !== undefined)
  @ExactlyOneDefined<CreateFormTemplateDto>(['federationId'])
  @IsUUID()
  medicalUnitId?: string;

  @IsObject()
  @IsNotEmptyObject()
  document: Record<string, unknown>;
}

export class CreateFormTemplateAssignmentDto extends ResolveFormTemplateAssignmentQueryDto {}

export type PublishFormTemplateResultDto = FormTemplateDto;

export interface CreateFormTemplateConflictDto {
  message: string;
  formTemplateId: string;
}

export interface FormTemplateAssignmentSummaryDto {
  id: string;
  conditionTypeId: string | null;
  specialityId: string | null;
  federationId: string | null;
  medicalUnitId: string | null;
  useCase: FormUseCase;
}

export interface FormTemplateSummaryDto {
  /** Logical parent UUID used for republishing. */
  id: string;
  created: string | Date;
  latestVersion: {
    id: string;
    version: number;
    formLanguageVersion: string;
    created: string | Date;
    updatedByUserName?: string;
  };
  assignments: FormTemplateAssignmentSummaryDto[];
}
